import React, { useCallback, useMemo, useState, useEffect } from "react";
import { LeftHandPanel } from "./LeftHandPanel";
import { RightHandPanel } from "./RightHandPanel";
import { ChatMode, useChatState } from "./hooks/useChatState";
import FluidButton from "ui/FluidButton";
import ConfirmationModal, { EConfirmationModalType } from "ui/ConfirmationModal";
import { chatTracking } from "./utils/tracking";
import { ChatHotjarRecording } from "./components/ChatHotjarRecording";

export interface IChat {
  conversationId: string;
  name: string;
  timestamp: string;
}

export const ChatPage = () => {
  const {
    state: {
      message,
      chats,
      activeChatId,
      messages,
      categories,
      chatMode,
      isPromptSelected,
      selectedCategory,
      prompts,
      pinnedChats,
      toDelete,
      toEdit,
      selectedChat,
      searchQuery,
      isLoadingChats,
    },
    actions: {
      setMessage,
      startNewChat,
      selectCategory,
      selectPrompt,
      backToCategories,
      sendMessage,
      selectChat,
      pinChat,
      deleteChat,
      selectChatToDelete,
      handleEditChatName,
      renameChat,
      handleSetChatToEdit,
      downloadChat,
      rateMessage,
      setSearchQuery,
    }
  } = useChatState();

  useEffect(() => {
    chatTracking.trackPageView();
  }, []);

  const handleCurrentMessage = useCallback((val: string) => {
    setMessage(val);
  }, []);

  const handlePressEnter = useCallback((event) => {
    if (event.key === 'Enter' && message.length > 0) {
      sendMessage();
    }
  }, [message]);

  const handleNewChatClick = useCallback(() => {
    chatTracking.trackNewChat();
    startNewChat();
  }, [startNewChat]);

  return (
    <>
      <ChatHotjarRecording />
      <div className="chat-page px-80px text-black font-pt-sans">
        <div className="flex justify-between">
          <h3 className="text-4xl leading-42px font-normal mt-0 mb-0 font-noe-display">Pure AI</h3>
          {chatMode === ChatMode.NORMAL && <FluidButton type="primary" onClick={handleNewChatClick}>New Chat</FluidButton>}
        </div>
        <div className="chat-container mt-20px flex gap-30px h-[calc(100vh-130px-40px-40px-168px-42px-20px)]">
          <LeftHandPanel
            selectedChat={activeChatId}
            chats={chats}
            pinnedChats={pinnedChats}
            editingChatName={toEdit}
            handleSelectChat={selectChat}
            searchQuery={searchQuery}
            isLoadingChats={isLoadingChats}
            pinChat={pinChat}
            selectChatToDelete={selectChatToDelete}
            handleEditChatName={handleEditChatName}
            handleSetChatToEdit={handleSetChatToEdit}
            renameChat={renameChat}
            setSearchQuery={setSearchQuery}
          />
          <RightHandPanel
            message={message}
            messages={messages}
            selectedChat={selectedChat}
            categories={categories}
            chatMode={chatMode}
            prompts={prompts}
            selectedCategory={selectedCategory}
            isPromptSelected={isPromptSelected}
            editingChatName={toEdit}
            sendMessage={sendMessage}
            handleCurrentMessage={handleCurrentMessage}
            handlePressEnter={handlePressEnter}
            backToCategories={backToCategories}
            selectCategory={selectCategory}
            selectPrompt={selectPrompt}
            pinChat={pinChat}
            selectChatToDelete={selectChatToDelete}
            handleEditChatName={handleEditChatName}
            handleSetChatToEdit={handleSetChatToEdit}
            renameChat={renameChat}
            downloadChat={downloadChat}
            handleRateMessage={rateMessage}
          />
        </div>
      </div>
      <ConfirmationModal
        type={EConfirmationModalType.WARNING}
        title="You are going to delete a chat"
        message="Are you sure?"
        isOpen={!!toDelete}
        onConfirm={deleteChat}
        onCancel={() => selectChatToDelete(null)}
        confirmButtonLabel="Yes"
        cancelButtonLabel="No"
      />
    </>
  )
}